import React, { useState } from 'react';
import {
  AlertTriangle,
  Send,
  FileText,
  User,
  CreditCard,
  Shield,
  MessageSquare,
} from 'lucide-react';
import MarketingReveal from '../../components/home/MarketingReveal';
import {
  MarketingPage,
  MarketingMediaHero,
  MarketingSection,
  MarketingSplitIntro,
} from '../../components/home/MarketingPageChrome';

const ReportProblem = () => {
  const [activeTab, setActiveTab] = useState('booking');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    problemType: 'booking',
    bookingId: '',
    vendorName: '',
    subject: '',
    description: '',
    attachments: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tabs = [
    { id: 'booking', label: 'Booking', icon: FileText },
    { id: 'payment', label: 'Payment', icon: CreditCard },
    { id: 'vendor', label: 'Vendor', icon: User },
    { id: 'safety', label: 'Safety', icon: Shield },
    { id: 'other', label: 'Other', icon: MessageSquare },
  ];

  const problemTypes = {
    booking: [
      { value: 'cancellation', label: 'Cancellation Issue' },
      { value: 'no-show', label: 'Vendor No-Show' },
      { value: 'service-quality', label: 'Service Quality' },
      { value: 'booking-error', label: 'Booking Error' },
      { value: 'refund', label: 'Refund Request' },
    ],
    payment: [
      { value: 'charge-error', label: 'Incorrect Charge' },
      { value: 'refund-delay', label: 'Refund Delay' },
      { value: 'payment-failed', label: 'Payment Failed' },
      { value: 'billing-error', label: 'Billing Error' },
    ],
    vendor: [
      { value: 'communication', label: 'Communication Issue' },
      { value: 'unprofessional', label: 'Unprofessional Behavior' },
      { value: 'contract-violation', label: 'Contract Violation' },
      { value: 'false-advertising', label: 'False Advertising' },
    ],
    safety: [
      { value: 'harassment', label: 'Harassment' },
      { value: 'safety-concern', label: 'Safety Concern' },
      { value: 'inappropriate', label: 'Inappropriate Behavior' },
      { value: 'fraud', label: 'Fraud or Scam' },
    ],
    other: [
      { value: 'technical', label: 'Technical Issue' },
      { value: 'account', label: 'Account Problem' },
      { value: 'general', label: 'General Inquiry' },
    ],
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setFormData((prev) => ({
      ...prev,
      problemType: tabId,
      subject: '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      alert(
        "Thank you for reporting this issue. We'll investigate and get back to you within 24 hours."
      );
      setFormData({
        name: '',
        email: '',
        phone: '',
        problemType: activeTab,
        bookingId: '',
        vendorName: '',
        subject: '',
        description: '',
        attachments: [],
      });
    }, 1000);
  };

  return (
    <MarketingPage>
      <MarketingMediaHero
        title="Report a problem"
        subtitle="We’re here to help resolve issues you’ve run into."
        height="sm"
      />

      <MarketingSection tone="white">
        <MarketingSplitIntro title="Tell us what happened">
          <p>
            Choose a category, share the details, and we’ll investigate. For urgent safety
            concerns, contact emergency services first — then report to us.
          </p>
        </MarketingSplitIntro>
      </MarketingSection>

      <MarketingSection tone="white" className="!pt-0 sm:!pt-0">
        <div className="mx-auto max-w-3xl">
          <MarketingReveal>
            <nav className="mb-8 flex gap-1 overflow-x-auto border-b border-m-border-light scrollbar-hide">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleTabChange(tab.id)}
                    className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-3 py-3 text-sm font-medium transition-colors ${
                      active
                        ? 'border-m-ink text-m-ink'
                        : 'border-transparent text-m-ink-muted hover:text-m-ink'
                    }`}
                  >
                    <IconComponent className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>

            <form onSubmit={handleSubmit} className="space-y-8">
              <div>
                <h2
                  className="mb-4 text-base font-bold tracking-tight"
                  style={{ color: '#0a0a0a' }}
                >
                  Your information
                </h2>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label className="auth-field-label" htmlFor="report-name">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="report-name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required
                      className="auth-input"
                    />
                  </div>
                  <div>
                    <label className="auth-field-label" htmlFor="report-email">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="report-email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      required
                      className="auth-input"
                    />
                  </div>
                </div>
                <div className="mt-5">
                  <label className="auth-field-label" htmlFor="report-phone">
                    Phone
                  </label>
                  <input
                    id="report-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 000-0000"
                    className="auth-input"
                  />
                </div>
              </div>

              <div>
                <h2
                  className="mb-4 text-base font-bold tracking-tight"
                  style={{ color: '#0a0a0a' }}
                >
                  Problem details
                </h2>
                <div className="space-y-5">
                  <div>
                    <label className="auth-field-label" htmlFor="report-subject">
                      Problem type <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="report-subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="auth-input"
                    >
                      <option value="">Select problem type</option>
                      {(problemTypes[activeTab] || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {activeTab === 'booking' ? (
                    <div>
                      <label className="auth-field-label" htmlFor="report-booking-id">
                        Booking ID
                      </label>
                      <input
                        id="report-booking-id"
                        name="bookingId"
                        value={formData.bookingId}
                        onChange={handleChange}
                        placeholder="Enter booking ID if available"
                        className="auth-input"
                      />
                    </div>
                  ) : null}

                  {activeTab === 'vendor' || activeTab === 'booking' ? (
                    <div>
                      <label className="auth-field-label" htmlFor="report-vendor-name">
                        Vendor name
                      </label>
                      <input
                        id="report-vendor-name"
                        name="vendorName"
                        value={formData.vendorName}
                        onChange={handleChange}
                        placeholder="Name of the vendor"
                        className="auth-input"
                      />
                    </div>
                  ) : null}

                  <div>
                    <label className="auth-field-label" htmlFor="report-description">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="report-description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Please provide a detailed description of the problem…"
                      rows={5}
                      required
                      className="auth-textarea"
                    />
                    <p className="mt-2 text-xs" style={{ color: '#6b6b6b' }}>
                      Include dates, times, and any details that will help us investigate.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end border-t border-m-border-light pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="marketing-btn marketing-btn-ink w-full gap-2 sm:w-auto disabled:opacity-60"
                >
                  <Send className="h-4 w-4" />
                  {isSubmitting ? 'Submitting…' : 'Submit report'}
                </button>
              </div>
            </form>
          </MarketingReveal>

          <MarketingReveal className="mt-10 flex items-start gap-3 border-t border-m-border-light pt-8">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" style={{ color: '#0a0a0a' }} />
            <div>
              <h3 className="mb-1 text-base font-bold" style={{ color: '#0a0a0a' }}>
                Important information
              </h3>
              <p className="mb-3 text-sm" style={{ color: '#6b6b6b' }}>
                We take all reports seriously. For urgent safety concerns, contact emergency
                services first, then our support line at 1-800-VENDA-911.
              </p>
              <p className="text-xs" style={{ color: '#8a8a8a' }}>
                Typical response within 24 hours · safety concerns within 4 hours.
              </p>
            </div>
          </MarketingReveal>
        </div>
      </MarketingSection>
    </MarketingPage>
  );
};

export default ReportProblem;
